import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { MagnifyingGlass, User, Shield, ShieldSlash, Key, CheckCircle, PencilSimple, Prohibit, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const ROLES = ['reader', 'writer', 'editor', 'moderator', 'admin'];
const ROLE_BADGE = {
  reader: 'bg-slate-100 text-slate-700 dark:bg-slate-900 dark:text-slate-300',
  writer: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  editor: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  moderator: 'bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300',
  admin: 'bg-primary/20 text-primary',
};

export default function CmsUsers() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (q) params.q = q;
      if (roleFilter) params.role = roleFilter;
      if (statusFilter) params.status = statusFilter;
      const { data } = await axios.get(`${API}/api/admin/cms/users`, { params, withCredentials: true });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [q, roleFilter, statusFilter]);

  const openDetail = async (u) => {
    setSelectedUser(u);
    setDetail(null);
    try {
      const { data } = await axios.get(`${API}/api/admin/cms/users/${u.user_id}`, { withCredentials: true });
      setDetail(data);
    } catch { toast.error('Failed to load profile'); }
  };

  const updateUser = async (user_id, patch, label) => {
    try {
      const { data } = await axios.patch(`${API}/api/admin/cms/users/${user_id}`, patch, { withCredentials: true });
      toast.success(label || 'Updated');
      setItems((curr) => curr.map((u) => u.user_id === user_id ? data.user : u));
      if (selectedUser?.user_id === user_id) setSelectedUser(data.user);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const resetPwd = async (user_id) => {
    if (!confirm('Send password reset email to this user?')) return;
    try {
      const { data } = await axios.post(`${API}/api/admin/cms/users/${user_id}/reset-password`, {}, { withCredentials: true });
      toast.success(data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    }
  };

  return (
    <div data-testid="cms-users">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>Users</h2>
          <p className="text-sm text-muted-foreground">{total} total</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or email…"
            data-testid="cms-users-search"
            className="w-full pl-9 pr-3 py-2 text-sm bg-muted/40 border border-transparent focus:border-primary focus:bg-background rounded-lg outline-none"
          />
        </div>
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} data-testid="cms-users-role-filter" className="text-sm border border-border rounded-lg px-3 py-2 bg-background">
          <option value="">All roles</option>
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="cms-users-status-filter" className="text-sm border border-border rounded-lg px-3 py-2 bg-background">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="banned">Banned</option>
          <option value="unverified">Unverified</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-3">User</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Joined</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="cms-users-tbody">
            {loading ? (
              <tr><td colSpan={5} className="text-center py-10 text-muted-foreground">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-10 text-muted-foreground">No users found</td></tr>
            ) : items.map((u) => (
              <tr key={u.user_id} className="border-t border-border hover:bg-muted/20">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-xs flex-shrink-0">
                      {(u.name || u.email || 'U').slice(0,1).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{u.name || '(no name)'}</p>
                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full ${ROLE_BADGE[u.role] || ROLE_BADGE.reader}`}>{u.role}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {u.is_banned && <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">Banned</span>}
                    {u.is_suspended && <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">Suspended</span>}
                    {u.email_verified === false && <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">Unverified</span>}
                    {u.is_verified_writer && <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Verified Writer</span>}
                    {!u.is_banned && !u.is_suspended && u.email_verified !== false && <span className="text-xs text-muted-foreground">Active</span>}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{(u.created_at || '').slice(0,10)}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => openDetail(u)}
                    data-testid={`cms-users-view-${u.user_id}`}
                    className="text-xs text-primary hover:underline"
                  >View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail drawer */}
      {selectedUser && (
        <>
          <div className="fixed inset-0 bg-background/70 backdrop-blur-sm z-40" onClick={() => setSelectedUser(null)} />
          <div className="fixed inset-y-0 right-0 w-full max-w-md bg-card border-l border-border z-50 overflow-y-auto" data-testid="cms-users-detail">
            <div className="p-5 border-b border-border flex items-center justify-between">
              <h3 className="font-semibold text-lg" style={{ fontFamily: 'Cormorant Garamond, serif' }}>User Detail</h3>
              <button onClick={() => setSelectedUser(null)} className="p-2 hover:bg-muted rounded-full"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-5">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-xl">
                  {(selectedUser.name || selectedUser.email || 'U').slice(0,1).toUpperCase()}
                </div>
                <div>
                  <p className="font-semibold">{selectedUser.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedUser.email}</p>
                </div>
              </div>

              {detail?.stats && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-muted/40 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground">Articles</p>
                    <p className="text-lg font-semibold">{detail.stats.articles}</p>
                  </div>
                  <div className="bg-muted/40 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground">Works</p>
                    <p className="text-lg font-semibold">{detail.stats.works}</p>
                  </div>
                  <div className="bg-muted/40 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground">Reviews</p>
                    <p className="text-lg font-semibold">{detail.stats.reviews}</p>
                  </div>
                  <div className="bg-muted/40 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground">Reading History</p>
                    <p className="text-lg font-semibold">{detail.stats.reading_history}</p>
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs uppercase text-muted-foreground block mb-2">Change Role</label>
                <div className="flex flex-wrap gap-1">
                  {ROLES.map((r) => (
                    <button
                      key={r}
                      onClick={() => updateUser(selectedUser.user_id, { role: r }, `Role updated to ${r}`)}
                      disabled={selectedUser.role === r}
                      className={`text-xs px-3 py-1.5 rounded-full ${selectedUser.role === r ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/70'}`}
                      data-testid={`cms-users-role-${r}`}
                    >{r}</button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <button
                  onClick={() => updateUser(selectedUser.user_id, { is_suspended: !selectedUser.is_suspended }, selectedUser.is_suspended ? 'Un-suspended' : 'Suspended')}
                  className="w-full inline-flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-muted text-sm"
                  data-testid="cms-users-suspend-toggle"
                >
                  {selectedUser.is_suspended ? <CheckCircle size={16} className="text-emerald-600" /> : <Prohibit size={16} className="text-orange-600" />}
                  {selectedUser.is_suspended ? 'Un-suspend' : 'Suspend user'}
                </button>
                <button
                  onClick={() => updateUser(selectedUser.user_id, { is_banned: !selectedUser.is_banned }, selectedUser.is_banned ? 'Un-banned' : 'Banned')}
                  className="w-full inline-flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-muted text-sm"
                  data-testid="cms-users-ban-toggle"
                >
                  {selectedUser.is_banned ? <ShieldSlash size={16} className="text-emerald-600" /> : <Shield size={16} className="text-red-600" />}
                  {selectedUser.is_banned ? 'Un-ban' : 'Ban user'}
                </button>
                <button
                  onClick={() => updateUser(selectedUser.user_id, { is_verified_writer: !selectedUser.is_verified_writer })}
                  className="w-full inline-flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-muted text-sm"
                  data-testid="cms-users-verify-writer"
                >
                  <CheckCircle size={16} className="text-emerald-600" />
                  {selectedUser.is_verified_writer ? 'Un-verify writer' : 'Verify writer'}
                </button>
                <button
                  onClick={() => resetPwd(selectedUser.user_id)}
                  className="w-full inline-flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-muted text-sm"
                  data-testid="cms-users-reset-password"
                >
                  <Key size={16} className="text-blue-600" /> Send password reset email
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
